<template>
  <div>
    <ContentWrap title="主播管理">
      <el-form :model="queryParams" ref="queryForm" :inline="true" v-show="showSearch">
        <el-form-item label="会员名" prop="userId">
          <LiveMemberUserSelect v-model="queryParams.userId" select-class="!w-260px" />
        </el-form-item>
        <el-form-item label="真实姓名" prop="realName">
          <el-input
            show-word-limit
            :maxlength="64"
            v-model="queryParams.realName"
            placeholder="请输入真实姓名"
            clearable
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="手机号码" prop="phone">
          <el-input
            show-word-limit
            :maxlength="20"
            v-model="queryParams.phone"
            placeholder="请输入手机号码"
            clearable
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="处理状态" prop="status">
          <el-select
            v-model="queryParams.status"
            placeholder="请选择状态"
            clearable
            style="width: 120px"
          >
            <el-option
              v-for="(label, key) in statusOptions"
              :key="key"
              :label="label"
              :value="Number(key)"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
          <el-button @click="resetQuery"><Icon icon="ep:refresh" />重置</el-button>
        </el-form-item>
      </el-form>

      <el-row :gutter="10" class="mb8">
        <el-col :span="1.5">
          <el-button type="primary" plain @click="handleAdd" v-hasPermi="['live:anchor:create']">
            <Icon icon="ep:plus" />新增
          </el-button>
        </el-col>
        <right-toolbar v-model:showSearch="showSearch" @query-table="getList" />
      </el-row>
    </ContentWrap>

    <ContentWrap>
      <el-table v-loading="loading" :data="list" border>
        <el-table-column label="编号" prop="id" width="80" />
        <el-table-column label="会员名" min-width="140">
          <template #default="scope">
            <div class="flex items-center gap-2">
              <el-avatar v-if="scope.row.avatar" :src="scope.row.avatar" :size="32" />
              <span>{{ scope.row.nickname || '--' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="性别" width="80" align="center">
          <template #default="scope">
            {{ sexLabel(scope.row.sex) }}
          </template>
        </el-table-column>
        <el-table-column label="手机号码" prop="phone" min-width="120" />
        <el-table-column label="身份证号码" prop="idCard" min-width="170" show-overflow-tooltip />
        <el-table-column label="真实姓名" prop="realName" min-width="100" />
        <el-table-column label="认证资料" width="120" align="center">
          <template #default="scope">
            <el-button
              v-if="scope.row.idCardFront || scope.row.idCardBack"
              link
              type="primary"
              @click="openCertPreview(scope.row)"
            >
              查看
            </el-button>
            <span v-else class="text-gray-400">未上传</span>
          </template>
        </el-table-column>
        <el-table-column
          label="申请时间"
          prop="createTime"
          min-width="170"
          :formatter="dateFormatter"
        />
        <el-table-column label="处理状态" prop="status" width="100">
          <template #default="scope">
            <el-tag :type="statusType(scope.row.status)">{{
              statusLabel(scope.row.status)
            }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="审核备注"
          prop="auditRemark"
          min-width="140"
          show-overflow-tooltip
        />
        <el-table-column label="操作" width="320" fixed="right">
          <template #default="scope">
            <el-button
              link
              type="primary"
              @click="handleEdit(scope.row)"
              v-hasPermi="['live:anchor:update']"
              >修改</el-button
            >
            <el-button
              link
              type="success"
              :disabled="Number(scope.row.status) !== 0"
              @click="handleAudit(scope.row)"
              v-hasPermi="['live:anchor:audit']"
              >审核</el-button
            >
            <el-button
              v-if="Number(scope.row.status) === 3"
              link
              type="success"
              v-hasPermi="['live:anchor:update']"
              @click="handleLivePermission(scope.row, true)"
              >开启权限</el-button
            >
            <el-button
              v-if="Number(scope.row.status) === 1"
              link
              type="warning"
              v-hasPermi="['live:anchor:update']"
              @click="handleLivePermission(scope.row, false)"
              >关闭权限</el-button
            >
            <el-button
              link
              type="danger"
              @click="handleDelete(scope.row)"
              v-hasPermi="['live:anchor:delete']"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <pagination
        v-show="total > 0"
        :total="total"
        v-model:page="queryParams.pageNo"
        v-model:limit="queryParams.pageSize"
        @pagination="getList"
      />
    </ContentWrap>

    <el-dialog :title="dialogTitle" v-model="dialogVisible" width="640px" append-to-body>
      <el-form
        ref="formRef"
        :model="form"
        :rules="withLiveInputLimits(rules, liveInputLimits)"
        label-width="110px"
      >
        <el-form-item label="会员用户" prop="userId">
          <LiveMemberUserSelect v-model="form.userId" select-class="!w-full" />
        </el-form-item>
        <el-form-item label="真实姓名" prop="realName">
          <el-input
            show-word-limit
            :maxlength="64"
            v-model="form.realName"
            placeholder="请输入真实姓名"
          />
        </el-form-item>
        <el-form-item label="身份证号" prop="idCard">
          <el-input
            show-word-limit
            :maxlength="18"
            v-model="form.idCard"
            placeholder="请输入身份证号"
          />
        </el-form-item>
        <el-form-item label="身份证人像面" prop="idCardFront">
          <UploadImg v-model="form.idCardFront" directory="live/anchor" />
        </el-form-item>
        <el-form-item label="身份证国徽面" prop="idCardBack">
          <UploadImg v-model="form.idCardBack" directory="live/anchor" />
        </el-form-item>
        <el-form-item label="手机号码" prop="phone">
          <el-input
            show-word-limit
            :maxlength="20"
            v-model="form.phone"
            placeholder="请输入手机号码"
          />
        </el-form-item>
        <el-form-item label="联系邮箱" prop="email">
          <el-input
            show-word-limit
            :maxlength="128"
            v-model="form.email"
            placeholder="请输入联系邮箱"
          />
        </el-form-item>
        <el-form-item label="主播头像" prop="avatar">
          <UploadImg v-model="form.avatar" />
        </el-form-item>
        <el-form-item label="主播简介" prop="intro">
          <el-input
            show-word-limit
            :maxlength="1000"
            v-model="form.intro"
            type="textarea"
            :rows="3"
            placeholder="请输入主播简介"
          />
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number
            :precision="0"
            v-model="form.sort"
            :min="0"
            :max="9999"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">已通过</el-radio>
            <el-radio :label="0">待审核</el-radio>
            <el-radio :label="2">已拒绝</el-radio>
            <el-radio :label="3">已禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="审核备注" prop="auditRemark">
          <el-input
            show-word-limit
            :maxlength="500"
            v-model="form.auditRemark"
            placeholder="请输入审核备注"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm">确 定</el-button>
      </template>
    </el-dialog>

    <el-dialog title="主播审核" v-model="auditVisible" width="640px" append-to-body>
      <el-form :model="auditForm" label-width="110px">
        <el-form-item label="会员名">
          <span>{{ auditForm.nickname || '--' }}</span>
        </el-form-item>
        <el-form-item label="性别">
          <span>{{ sexLabel(auditForm.sex) }}</span>
        </el-form-item>
        <el-form-item label="真实姓名">
          <span>{{ auditForm.realName || '--' }}</span>
        </el-form-item>
        <el-form-item label="手机号码">
          <span>{{ auditForm.phone || '--' }}</span>
        </el-form-item>
        <el-form-item label="身份证号码">
          <span>{{ auditForm.idCard || '--' }}</span>
        </el-form-item>
        <el-form-item label="申请时间">
          <span>{{ formatDate(auditForm.createTime) }}</span>
        </el-form-item>
        <el-form-item label="认证资料">
          <div v-if="certPreviewList.length" class="flex gap-3 flex-wrap">
            <el-image
              v-for="(img, idx) in certPreviewList"
              :key="idx"
              :src="img"
              :preview-src-list="certPreviewList"
              :initial-index="idx"
              fit="cover"
              preview-teleported
              style="width: 140px; height: 90px; border-radius: 6px"
            />
          </div>
          <span v-else class="text-gray-400">未上传身份证影像</span>
        </el-form-item>
        <el-form-item label="审核状态" prop="status">
          <el-radio-group v-model="auditForm.status">
            <el-radio :label="1">通过</el-radio>
            <el-radio :label="2">拒绝</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="审核备注" prop="auditRemark">
          <el-input
            show-word-limit
            :maxlength="500"
            v-model="auditForm.auditRemark"
            type="textarea"
            :rows="3"
            placeholder="请输入审核备注"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="auditVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitAudit">确 定</el-button>
      </template>
    </el-dialog>

    <el-dialog title="认证资料" v-model="certVisible" width="560px" append-to-body>
      <div class="mb-3 text-sm text-gray-600">
        <div>会员名：{{ certRow?.nickname || '--' }}</div>
        <div>真实姓名：{{ certRow?.realName || '--' }}</div>
        <div>身份证号码：{{ certRow?.idCard || '--' }}</div>
      </div>
      <div v-if="certLabeledList.length" class="flex gap-3 flex-wrap">
        <div v-for="(item, idx) in certLabeledList" :key="idx" class="text-center">
          <el-image
            :src="item.url"
            :preview-src-list="certLabeledList.map((i) => i.url)"
            :initial-index="idx"
            fit="cover"
            preview-teleported
            style="width: 220px; height: 140px; border-radius: 6px"
          />
          <div class="mt-1 text-xs text-gray-500">{{ item.label }}</div>
        </div>
      </div>
      <span v-else class="text-gray-400">未上传身份证影像</span>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { withLiveInputLimits } from '@/utils/liveInputLimits'
const liveInputLimits = {
  realName: 64,
  idCard: 18,
  idCardFront: 512,
  idCardBack: 512,
  phone: 20,
  email: 128,
  intro: 1000,
  auditRemark: 500,
  avatar: 512
}

import { dateFormatter, formatDate } from '@/utils/formatTime'
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  pageAnchor,
  createAnchor,
  updateAnchor,
  deleteAnchor,
  auditAnchor,
  setLivePermission
} from '@/api/live/anchor'
import { UploadImg } from '@/components/UploadFile'
import LiveMemberUserSelect from '@/views/live/components/LiveMemberUserSelect.vue'

const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const showSearch = ref(true)
const dialogVisible = ref(false)
const dialogTitle = ref('')
const formRef = ref<any>(null)
const auditVisible = ref(false)
const certVisible = ref(false)
const certRow = ref<any>(null)

const statusOptions: Record<number, string> = {
  0: '待审核',
  1: '已通过',
  2: '已拒绝',
  3: '已禁用'
}

const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  userId: undefined as number | undefined,
  realName: '',
  phone: '',
  status: undefined as number | undefined
})

const form = reactive({
  id: undefined as number | undefined,
  userId: undefined as number | undefined,
  realName: '',
  idCard: '',
  idCardFront: '',
  idCardBack: '',
  phone: '',
  email: '',
  intro: '',
  avatar: '',
  status: 1,
  auditRemark: '',
  sort: 0
})

const auditForm = reactive({
  id: undefined as number | undefined,
  nickname: '',
  sex: undefined as number | undefined,
  realName: '',
  phone: '',
  idCard: '',
  idCardFront: '',
  idCardBack: '',
  createTime: '' as string | undefined,
  status: 1,
  auditRemark: ''
})

const buildCertLabeled = (row: any) => {
  const items: { label: string; url: string }[] = []
  if (row?.idCardFront) items.push({ label: '人像面', url: row.idCardFront })
  if (row?.idCardBack) items.push({ label: '国徽面', url: row.idCardBack })
  return items
}

const certLabeledList = computed(() => buildCertLabeled(certRow.value))

const certPreviewList = computed(() => {
  const row = certVisible.value ? certRow.value : auditForm
  return buildCertLabeled(row).map((i) => i.url)
})

const rules = {
  userId: [{ required: true, message: '会员用户不能为空', trigger: 'change' }],
  realName: [{ required: true, message: '真实姓名不能为空', trigger: 'blur' }]
}

const sexLabel = (sex?: number) => {
  if (sex === 1) return '男'
  if (sex === 2) return '女'
  return '未知'
}

const getList = async () => {
  loading.value = true
  try {
    const data: any = await pageAnchor(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.userId = undefined
  queryParams.realName = ''
  queryParams.phone = ''
  queryParams.status = undefined
  queryParams.pageNo = 1
  getList()
}

const resetForm = () => {
  form.id = undefined
  form.userId = undefined
  form.realName = ''
  form.idCard = ''
  form.idCardFront = ''
  form.idCardBack = ''
  form.phone = ''
  form.email = ''
  form.intro = ''
  form.avatar = ''
  form.status = 1
  form.auditRemark = ''
  form.sort = 0
}

const handleAdd = () => {
  resetForm()
  dialogTitle.value = '新增主播'
  dialogVisible.value = true
}

const handleEdit = (row: any) => {
  resetForm()
  Object.assign(form, {
    id: row.id,
    userId: row.userId,
    realName: row.realName || '',
    idCard: row.idCard || '',
    idCardFront: row.idCardFront || '',
    idCardBack: row.idCardBack || '',
    phone: row.phone || '',
    email: row.email || '',
    intro: row.intro || '',
    avatar: row.avatar || '',
    status: row.status ?? 1,
    auditRemark: row.auditRemark || '',
    sort: row.sort ?? 0
  })
  dialogTitle.value = '修改主播'
  dialogVisible.value = true
}

const submitForm = async () => {
  await formRef.value?.validate()
  if (form.id) {
    await updateAnchor(form as any)
    ElMessage.success('修改成功')
  } else {
    await createAnchor(form as any)
    ElMessage.success('新增成功')
  }
  dialogVisible.value = false
  getList()
}

const handleDelete = (row: any) => {
  ElMessageBox.confirm('确认删除该主播吗？', '提示', { type: 'warning' }).then(async () => {
    await deleteAnchor(row.id)
    ElMessage.success('删除成功')
    getList()
  })
}

const handleLivePermission = async (row: any, allow: boolean) => {
  if (!row?.userId) {
    ElMessage.warning('缺少会员用户编号')
    return
  }
  await ElMessageBox.confirm(
    allow ? '确认开启该主播的直播权限？' : '确认关闭该主播的直播权限？',
    '提示',
    { type: 'warning' }
  )
  await setLivePermission(row.userId, allow)
  ElMessage.success(allow ? '已开启直播权限' : '已关闭直播权限')
  getList()
}

const statusLabel = (status: number) => statusOptions[status] || '未知'
const statusType = (status: number) => {
  switch (status) {
    case 1:
      return 'success'
    case 0:
      return 'warning'
    case 2:
      return 'danger'
    case 3:
      return 'info'
    default:
      return ''
  }
}

const openCertPreview = (row: any) => {
  certRow.value = row
  certVisible.value = true
}

const handleAudit = (row: any) => {
  if (Number(row.status) !== 0) return
  auditForm.id = row.id
  auditForm.nickname = row.nickname || ''
  auditForm.sex = row.sex
  auditForm.realName = row.realName || ''
  auditForm.phone = row.phone || ''
  auditForm.idCard = row.idCard || ''
  auditForm.idCardFront = row.idCardFront || ''
  auditForm.idCardBack = row.idCardBack || ''
  auditForm.createTime = row.createTime
  auditForm.status = 1
  auditForm.auditRemark = row.auditRemark || ''
  auditVisible.value = true
}

const submitAudit = async () => {
  if (auditForm.auditRemark.length > 500) {
    ElMessage.warning('审核备注最多500个字符')
    return
  }
  if (!auditForm.id) return
  if (auditForm.status !== 1 && auditForm.status !== 2) {
    ElMessage.warning('请选择通过或拒绝')
    return
  }
  await auditAnchor(auditForm.id, auditForm.status, auditForm.auditRemark)
  ElMessage.success('审核成功')
  auditVisible.value = false
  getList()
}

onMounted(() => {
  const route = useRoute()
  const uid = Number(route.query.userId)
  if (Number.isFinite(uid) && uid > 0) {
    queryParams.userId = uid
  }
  getList()
})
</script>
